<template>
  <div class="map-library-view">
    <b-card class="mb-1">
      <div class="d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <h4 class="mb-0">
            أقرب مكتبات خرائط
          </h4>
          <small class="text-muted">يتم استخدام موقعك الحالي لإحضار أقرب مكتبات الخرائط.</small>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            variant="primary"
            class="mr-50"
            :disabled="locationLoading || mapLoading"
            @click="refreshLocation"
          >
            <b-spinner
              v-if="locationLoading"
              small
              class="mr-50"
            />
            تحديث الموقع
          </b-button>
        </div>
      </div>
      <div class="mt-1 text-muted">
        إحداثيات الموقع:
        <span v-if="currentPoint">
          {{ currentPoint.latitude.toFixed(6) }}, {{ currentPoint.longitude.toFixed(6) }}
        </span>
        <span v-else>لم يتم تحديد الموقع بعد</span>
      </div>
    </b-card>

    <div class="map-container-wrapper mb-1">
      <div
        v-if="mapLoading"
        class="loading-overlay"
      >
        <div class="spinner" />
      </div>
      <div
        ref="esriView"
        class="map-view"
      />
    </div>

    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-1">
        <h5 class="mb-0">
          عرض النتائج على الخريطة
        </h5>
        <b-spinner
          v-if="mapsLoading"
          small
        />
      </div>
      <b-alert
        v-if="!mapsLoading && !(mapsByCurrentLocations && mapsByCurrentLocations.length)"
        variant="warning"
        show
      >
        لا توجد بيانات للعرض بالقرب من موقعك.
      </b-alert>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { loadArcgisModules } from '@/utils/arcgisLoader'
import {
  BCard,
  BButton,
  BSpinner,
  BAlert,
} from 'bootstrap-vue'

export default {
  name: 'ViewMapLibrary',
  components: {
    BCard,
    BButton,
    BSpinner,
    BAlert,
  },
  data() {
    return {
      map: null,
      view: null,
      pointLayer: null,
      polygonLayer: null,
      fixedPointsLayer: null,
      arcgisModules: null,
      mapLoading: false,
      locationLoading: false,
      currentPoint: null,
      defaultPoint: {
        latitude: 31.2001, // Cairo fallback
        longitude: 29.9187,
      },
      stationaryTimer: null,
      initialLocationSet: false,
    }
  },
  computed: {
    ...mapGetters({
      mapsByCurrentLocations: 'mapLibraries/mapsByCurrentLocations',
      mapsLoading: 'mapLibraries/loadMapsByCurrentLocations',
      fixedPoints: 'fixedPoints/fixedPoints',
      fixedPointsLoading: 'fixedPoints/load',
    }),
  },
  watch: {
    mapsByCurrentLocations: {
      handler() {
        this.renderResultPolygons()
      },
      deep: true,
    },
    fixedPoints: {
      handler() {
        this.renderFixedPoints()
      },
      deep: true,
    },
  },
  mounted() {
    this.initMap()
  },
  beforeDestroy() {
    if (this.view) {
      this.view.destroy()
    }
  },
  methods: {
    async initMap() {
      this.mapLoading = true
      try {
        const modules = await loadArcgisModules(
          [
            'esri/Map',
            'esri/views/MapView',
            'esri/layers/GraphicsLayer',
            'esri/Graphic',
            'esri/geometry/Point',
            'esri/symbols/SimpleMarkerSymbol',
            'esri/symbols/PictureMarkerSymbol',
            'esri/geometry/Polygon',
            'esri/symbols/SimpleFillSymbol',
            'esri/widgets/Search',
            'esri/geometry/Extent',
          ],
        )

        this.arcgisModules = {
          Map: modules[0],
          MapView: modules[1],
          GraphicsLayer: modules[2],
          Graphic: modules[3],
          Point: modules[4],
          SimpleMarkerSymbol: modules[5],
          PictureMarkerSymbol: modules[6],
          Polygon: modules[7],
          SimpleFillSymbol: modules[8],
          Search: modules[9],
          Extent: modules[10],
        }

        this.pointLayer = new this.arcgisModules.GraphicsLayer()
        this.polygonLayer = new this.arcgisModules.GraphicsLayer()
        this.fixedPointsLayer = new this.arcgisModules.GraphicsLayer({
          title: 'Fixed Points',
          visible: true,
        })
        this.map = new this.arcgisModules.Map({
          // basemap: 'streets-navigation-vector',
          basemap: 'hybrid',
          layers: [this.polygonLayer, this.fixedPointsLayer, this.pointLayer],
        })
        console.log('Map initialized with fixed points layer')

        this.view = new this.arcgisModules.MapView({
          container: this.$refs.esriView,
          map: this.map,
          center: [this.defaultPoint.longitude, this.defaultPoint.latitude],
          zoom: 8,
          popupEnabled: false,
        })

        this.addSearchWidget()
        this.bindViewEvents()
        this.bindMapClickTooltip()
        await this.refreshLocation()
        this.fetchFixedPoints()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to initialize Esri map', error)
      } finally {
        this.mapLoading = false
      }
    },
    async refreshLocation() {
      if (this.locationLoading) return
      this.locationLoading = true
      try {
        const coords = await this.getBrowserLocation()
        this.currentPoint = {
          latitude: coords.latitude,
          longitude: coords.longitude,
        }
      } catch (error) {
        this.currentPoint = { ...this.defaultPoint }
        // eslint-disable-next-line no-console
        console.warn('Using fallback location after geolocation failure', error)
      } finally {
        this.locationLoading = false
      }
      // Only change center on initial load, not after data loads
      const isInitialLoad = !this.initialLocationSet
      this.updateMapMarker({ skipGoTo: !isInitialLoad })
      if (isInitialLoad) {
        this.initialLocationSet = true
      }
      this.GetMapsByCurrentLocations()
    },
    addSearchWidget() {
      if (!this.view || !this.arcgisModules) return
      const search = new this.arcgisModules.Search({
        view: this.view,
        includeDefaultSources: true,
      })
      search.on('select', () => {
        this.updateLocationFromView()
      })
      search.on('search-complete', () => {
        this.updateLocationFromView()
      })
      this.view.ui.add(search, 'top-right')
    },
    bindViewEvents() {
      if (!this.view) return
      this.view.watch('stationary', isStationary => {
        if (!isStationary) return
        if (this.stationaryTimer) clearTimeout(this.stationaryTimer)
        this.stationaryTimer = setTimeout(() => {
          this.updateLocationFromView()
        }, 250)
      })
      this.view.on('drag', event => {
        if (event.action === 'end') {
          this.updateLocationFromView()
        }
      })
    },
    bindMapClickTooltip() {
      if (!this.view) return
      this.view.on('click', async event => {
        const hit = await this.view.hitTest(event, {
          include: [this.polygonLayer, this.fixedPointsLayer],
        })

        // Check for fixed points first (they're on top)
        const fixedPointResult = hit?.results?.find(
          r => r.graphic && r.graphic.layer === this.fixedPointsLayer,
        )
        if (fixedPointResult) {
          this.view.popup.open({
            location: fixedPointResult.mapPoint,
            features: [fixedPointResult.graphic],
            updateLocationEnabled: false,
          })
          return
        }

        // Check for polygons
        const polygonResult = hit?.results?.find(
          r => r.graphic && r.graphic.layer === this.polygonLayer,
        )
        if (polygonResult) {
          this.view.popup.open({
            location: polygonResult.mapPoint,
            features: [polygonResult.graphic],
            updateLocationEnabled: false,
          })
          return
        }

        // Close popup if clicking on empty space
        this.view.popup.close()
      })
    },
    getBrowserLocation() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation not supported'))
          return
        }
        navigator.geolocation.getCurrentPosition(
          pos => resolve(pos.coords),
          reject,
          { enableHighAccuracy: true, timeout: 5000 },
        )
      })
    },
    updateMapMarker(options = {}) {
      const { skipGoTo = false } = options
      if (!this.currentPoint || !this.arcgisModules || !this.pointLayer) return
      const { Point, Graphic, SimpleMarkerSymbol } = this.arcgisModules
      this.pointLayer.removeAll()
      const point = new Point({
        latitude: this.currentPoint.latitude,
        longitude: this.currentPoint.longitude,
      })

      const symbol = new SimpleMarkerSymbol({
        color: [0, 153, 255, 0.9],
        outline: {
          color: [255, 255, 255],
          width: 2,
        },
        size: '14px',
      })

      const graphic = new Graphic({
        geometry: point,
        symbol,
      })

      this.pointLayer.add(graphic)
      // Only change center if skipGoTo is false (i.e., on initial load)
      if (this.view && !skipGoTo) {
        this.view.goTo({ center: [this.currentPoint.longitude, this.currentPoint.latitude], zoom: 8 })
      }
    },
    updateLocationFromView() {
      if (!this.view) return
      const { latitude, longitude } = this.view.center
      const sameLocation = this.currentPoint
        && Math.abs(this.currentPoint.latitude - latitude) < 1e-7
        && Math.abs(this.currentPoint.longitude - longitude) < 1e-7
      if (sameLocation) return
      this.currentPoint = { latitude, longitude }
      this.updateMapMarker({ skipGoTo: true })
      this.GetMapsByCurrentLocations()
    },
    GetMapsByCurrentLocations() {
      if (!this.currentPoint) return
      const payload = {
        latitude: this.currentPoint.latitude,
        longitude: this.currentPoint.longitude,
      }
      this.$store.dispatch('mapLibraries/GetMapsByCurrentLocations', payload)
    },
    async fetchFixedPoints() {
      try {
        await this.$store.dispatch('fixedPoints/listFixedPoints', {
          query: {
            per_page: 1000, // Get more fixed points for the map
          },
        })
        // Manually trigger render after data is loaded
        this.$nextTick(() => {
          this.renderFixedPoints()
        })
      } catch (error) {
        console.error('Error fetching fixed points:', error)
      }
    },
    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result
        ? [
          parseInt(result[1], 16),
          parseInt(result[2], 16),
          parseInt(result[3], 16),
          0.9,
        ]
        : [0, 153, 255, 0.9]
    },
    createPinIcon(colorHex) {
      // Create an SVG pin/marker icon with the specified color
      // The pin point is at the bottom center (coordinates location)
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
          <!-- Pin shadow -->
          <ellipse cx="16" cy="36" rx="4" ry="2" fill="rgba(0,0,0,0.2)"/>
          <!-- Pin body -->
          <path d="M16 2C10.477 2 6 6.477 6 12c0 6 10 24 10 24s10-18 10-24C26 6.477 21.523 2 16 2z" 
                fill="${colorHex}" 
                stroke="#ffffff" 
                stroke-width="2"/>
          <!-- Pin center circle -->
          <circle cx="16" cy="12" r="5" fill="#ffffff"/>
          <circle cx="16" cy="12" r="3" fill="${colorHex}"/>
        </svg>
      `.trim()
      return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`
    },
    renderFixedPoints() {
      if (!this.fixedPointsLayer || !this.arcgisModules) {
        console.log('Fixed points layer or modules not ready')
        return
      }
      const { Point, Graphic, SimpleMarkerSymbol } = this.arcgisModules
      this.fixedPointsLayer.removeAll()

      // fixedPoints is already an array
      const fixedPointsData = Array.isArray(this.fixedPoints) ? this.fixedPoints : (this.fixedPoints?.data || [])
      console.log('Fixed points data:', fixedPointsData)
      console.log('Fixed points count:', fixedPointsData.length)

      if (!fixedPointsData.length) {
        console.log('No fixed points data to render')
        return
      }

      let renderedCount = 0
      fixedPointsData.forEach(fixedPoint => {
        // Parse latitude/longitude as numbers (they come as strings from API)
        const lat = parseFloat(fixedPoint.latitude)
        const lng = parseFloat(fixedPoint.longitude)

        // Use latitude/longitude if available, otherwise skip
        if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
          console.log('Skipping fixed point (invalid coordinates):', fixedPoint.code || fixedPoint.id, 'lat:', fixedPoint.latitude, 'lng:', fixedPoint.longitude)
          return
        }

        const point = new Point({
          latitude: lat,
          longitude: lng,
        })

        // Get color from accuracy_degree_color or default
        const colorHex = fixedPoint.accuracy_degree_color || '#0099ff'

        // Create a better pin-style marker using SVG with anchor point
        const pinSvg = this.createPinIcon(colorHex)

        // Use PictureMarkerSymbol for custom icon
        const { PictureMarkerSymbol } = this.arcgisModules
        const symbol = new PictureMarkerSymbol({
          url: pinSvg,
          width: 32,
          height: 40, // Taller to show the pin point
          xoffset: 0,
          yoffset: 20, // Offset to position the pin point at the coordinates
        })

        // Build tooltip content
        const tooltipContent = this.buildFixedPointTooltip(fixedPoint)

        const graphic = new Graphic({
          geometry: point,
          symbol,
          attributes: {
            id: fixedPoint.id,
            code: fixedPoint.code,
            latitude: lat,
            longitude: lng,
            level: fixedPoint.level,
            accuracy_degree: fixedPoint.accuracy_degree_label_ar || fixedPoint.accuracy_degree,
            accuracy_degree_color: fixedPoint.accuracy_degree_color,
            responsible_employee: fixedPoint.responsible_employee
              ? fixedPoint.responsible_employee.full_name
              : '-',
            etm_est: fixedPoint.etm_est,
            etm_north: fixedPoint.etm_north,
            etm_zone: fixedPoint.etm_zone,
            utm_est: fixedPoint.utm_est,
            utm_north: fixedPoint.utm_north,
            utm_zone: fixedPoint.utm_zone,
            created_at: fixedPoint.created_at,
          },
          popupTemplate: {
            title: fixedPoint.code || 'نقطة ثابتة',
            content: tooltipContent,
          },
        })

        this.fixedPointsLayer.add(graphic)
        renderedCount++
      })
      console.log(`Rendered ${renderedCount} fixed point markers on the map`)

      // Don't change the center after loading data - keep it at user's location
      // if (renderedCount > 0 && this.view) {
      //   this.fitViewToFixedPoints(fixedPointsData.filter(fp => {
      //     const lat = parseFloat(fp.latitude);
      //     const lng = parseFloat(fp.longitude);
      //     return lat && lng && !isNaN(lat) && !isNaN(lng);
      //   }));
      // }
    },
    fitViewToFixedPoints(fixedPoints) {
      // Don't change center after initial location is set
      if (this.initialLocationSet) {
        console.log('Skipping fitViewToFixedPoints - initial location already set')
        return
      }
      if (!this.view || !fixedPoints || !fixedPoints.length) {
        console.log('Cannot fit view: view or fixedPoints not available')
        return
      }

      try {
        const { Extent } = this.arcgisModules
        if (!Extent) {
          console.error('Extent module not loaded')
          return
        }

        const coordinates = fixedPoints
          .map(fp => {
            const lng = parseFloat(fp.longitude)
            const lat = parseFloat(fp.latitude)
            return { lng, lat }
          })
          .filter(coord => coord.lng && coord.lat && !isNaN(coord.lng) && !isNaN(coord.lat))

        if (coordinates.length === 0) {
          console.log('No valid coordinates to fit view')
          return
        }

        // Calculate bounding box
        const lngs = coordinates.map(c => c.lng)
        const lats = coordinates.map(c => c.lat)
        const minLng = Math.min(...lngs)
        const maxLng = Math.max(...lngs)
        const minLat = Math.min(...lats)
        const maxLat = Math.max(...lats)

        // Add padding (10% of the range)
        const lngRange = maxLng - minLng
        const latRange = maxLat - minLat
        const lngPadding = lngRange > 0 ? lngRange * 0.1 : 0.01
        const latPadding = latRange > 0 ? latRange * 0.1 : 0.01

        const extent = new Extent({
          xmin: minLng - lngPadding,
          ymin: minLat - latPadding,
          xmax: maxLng + lngPadding,
          ymax: maxLat + latPadding,
          spatialReference: { wkid: 4326 },
        })

        console.log('Fitting view to extent:', extent)
        this.view.goTo(extent, { duration: 1000 }).catch(err => {
          console.warn('Error fitting view to fixed points:', err)
          // Fallback: center on first point
          if (coordinates.length > 0) {
            this.view.goTo({
              center: [coordinates[0].lng, coordinates[0].lat],
              zoom: 12,
            })
          }
        })
      } catch (error) {
        console.error('Error in fitViewToFixedPoints:', error)
      }
    },
    buildFixedPointTooltip(fixedPoint) {
      const fields = [
        { fieldName: 'code', label: 'الكود' },
        { fieldName: 'level', label: 'المستوى' },
        { fieldName: 'accuracy_degree', label: 'درجة الدقة' },
        { fieldName: 'responsible_employee', label: 'الموظف المسؤول' },
      ]

      // Add coordinates if available
      if (fixedPoint.latitude && fixedPoint.longitude) {
        fields.push(
          {
            fieldName: 'latitude',
            label: 'خط العرض',
            format: {
              places: 6,
              digitSeparator: true,
            },
          },
          {
            fieldName: 'longitude',
            label: 'خط الطول',
            format: {
              places: 6,
              digitSeparator: true,
            },
          },
        )
      }

      // Add ETM if available
      if (fixedPoint.etm_est && fixedPoint.etm_north) {
        fields.push(
          { fieldName: 'etm_est', label: 'ETM East' },
          { fieldName: 'etm_north', label: 'ETM North' },
          { fieldName: 'etm_zone', label: 'ETM Zone' },
        )
      }

      // Add UTM if available
      if (fixedPoint.utm_est && fixedPoint.utm_north) {
        fields.push(
          { fieldName: 'utm_est', label: 'UTM East' },
          { fieldName: 'utm_north', label: 'UTM North' },
          { fieldName: 'utm_zone', label: 'UTM Zone' },
        )
      }

      return [
        {
          type: 'fields',
          fieldInfos: fields,
        },
      ]
    },
    renderResultPolygons() {
      if (!this.polygonLayer || !this.arcgisModules) return
      const { Polygon, SimpleFillSymbol, Graphic } = this.arcgisModules
      this.polygonLayer.removeAll()
      const items = this.normalizeMapItems(this.mapsByCurrentLocations)
      if (!items.length) return

      items.forEach(item => {
        const geometry = this.buildPolygonGeometry(item)
        if (!geometry) return

        const polygon = new Polygon(geometry)
        const fillSymbol = new SimpleFillSymbol({
          color: [0, 153, 255, 0.1],
          outline: {
            color: [0, 153, 255, 0.8],
            width: 2,
          },
        })

        const areaRaw = Number(item.shape_area || item?.properties?.SHAPE_AREA)
        const areaSqKm = Number.isFinite(areaRaw) ? areaRaw : null
        const distanceMeters = Number(item.distance_meters || item.distance)

        const graphic = new Graphic({
          geometry: polygon,
          symbol: fillSymbol,
          attributes: {
            name: item.name || item?.properties?.name || 'غير مسمى',
            governorate: item?.properties?.gov_name,
            city: item?.properties?.city_name || item?.properties?.city || item?.properties?.village_name,
            district: item?.properties?.locality_name,
            description: item.description || item?.properties?.description,
            area: item.shape_area || item?.properties?.SHAPE_AREA,
            area_sq_km: areaSqKm,
            length: item.shape_leng || item?.properties?.SHAPE_LENG,
            distance_meters: distanceMeters,
          },
          popupTemplate: {
            title: '{name}',
            content: [
              {
                type: 'fields',
                fieldInfos: [
                  { fieldName: 'governorate', label: 'المحافظة' },
                  { fieldName: 'city', label: 'المدينة' },
                  { fieldName: 'district', label: 'الحي / القرية' },
                  { fieldName: 'distance_meters', label: 'المسافة (متر)' },
                  { fieldName: 'area', label: 'المساحة (الوحدة الأصلية)' },
                  { fieldName: 'area_sq_km', label: 'المساحة (كم²)' },
                  { fieldName: 'length', label: 'المحيط' },
                  { fieldName: 'description', label: 'الوصف' },
                ],
              },
            ],
          },
        })

        this.polygonLayer.add(graphic)
      })
    },
    normalizeMapItems(data) {
      if (Array.isArray(data)) return data
      if (data?.mapLibrary) {
        const val = Object.values(data.mapLibrary)[0]
        if (Array.isArray(val)) return val
      }
      if (data?.data && Array.isArray(data.data)) return data.data
      return []
    },
    buildPolygonGeometry(item) {
      const spatialReference = { wkid: 4326 }
      const normalizeRings = coords => {
        if (!Array.isArray(coords) || !coords.length) return null
        // Polygon: [ [ [x,y,...], ... ] ]
        if (typeof coords[0]?.[0] === 'number') return [coords]
        // MultiPolygon or nested: flatten one level
        if (Array.isArray(coords[0]) && typeof coords[0][0]?.[0] === 'number') return coords
        if (Array.isArray(coords[0]) && Array.isArray(coords[0][0])) {
          return coords.flat()
        }
        return null
      }

      if (item?.geometry_geojson) {
        try {
          const geo = typeof item.geometry_geojson === 'string'
            ? JSON.parse(item.geometry_geojson)
            : item.geometry_geojson
          if (geo?.coordinates) {
            const coords = geo.type === 'Polygon' ? geo.coordinates : geo.coordinates?.flat?.() || geo.coordinates
            const rings = normalizeRings(coords)
            if (rings) return { rings, spatialReference }
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('Failed to parse geometry_geojson', e)
        }
      }

      if (item?.geometry?.coordinates) {
        const coords = item.geometry.type === 'Polygon'
          ? item.geometry.coordinates
          : item.geometry.coordinates?.flat?.() || item.geometry.coordinates
        const rings = normalizeRings(coords)
        if (rings) return { rings, spatialReference }
      }

      return null
    },
  },
}
</script>

<style scoped>
.map-view {
  height: 520px;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.map-container-wrapper {
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.spinner {
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 4px solid #06284D;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
